<template>
    <v-card>
        <v-card-title>Add Schema</v-card-title>
        <v-card-text>
            <v-tabs v-model="tab">
                <v-tab v-if="!isTemplateConfig" value="AddFromTemplate">Schema Templates</v-tab>
                <v-tab value="AddBlank">Blank Schema</v-tab>
            </v-tabs>
            <v-container fluid v-if="tab === 'AddFromTemplate'">
                <v-row align="center">
                    <v-col cols="4">
                        <v-text-field label="search" v-model="searchTemplateSchemas" clearable />
                    </v-col>
                </v-row>
                <v-data-table :items="thingSchemaTemplates"
                              :headers="thingSchemaTemplateHeaders"
                              item-key="id"
                              show-select
                              return-object
                              :search="searchTemplateSchemas"
                              v-model="selectedThingSchemaTemplates"
                              density="compact"
                >
                </v-data-table>
            </v-container>
            <v-container fluid v-if="tab === 'AddBlank'">
                <v-row>
                    <v-col>
                        <v-text-field label="Schema name" v-model="name" />
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
        <v-card-actions>
            <v-spacer/>
            <v-btn @click="cancel" color="warning">Cancel</v-btn>
            <v-btn @click="add" :disabled="isAddDisabled" color="primary">{{ addButtonText }}</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import {
    manageSchemasAPI,
    SavedThingSchemaTemplate,
} from "@/services/schemas/manageSchemasAPI";
import { withLoadingScreen } from "@/lib/loading";
import { VDataTableHeader } from "@/vuetifyTypeFixes";

type Tab = "AddFromTemplate" | "AddBlank"

const { existingThingSchemaNames } = defineProps<{
    existingThingSchemaNames: string[],
    isTemplateConfig: boolean,
}>();

const emit = defineEmits<{
    (e: 'cancelForm' ): void,
    (e: 'addThingSchemaTemplates', thingSchemaTemplates: SavedThingSchemaTemplate[]);
    (e: 'addBlankThingSchema', name: string);
}>()

const tab = ref<Tab>("AddFromTemplate");
const thingSchemaTemplates = ref<SavedThingSchemaTemplate[]>([]);
const selectedThingSchemaTemplates = ref<SavedThingSchemaTemplate[]>([]);
const thingSchemaTemplateHeaders = ref<VDataTableHeader[]>([
    {
        title: "Display Name",
        value: "displayName",
        sortable: true,
        align: "start",
    },
    {
        title: "Name",
        value: "name",
        sortable: true,
        align: "start",
    },
]);
const searchTemplateSchemas = ref("");
const name = ref("");

const addButtonText = computed(() => {
    if (tab.value === "AddFromTemplate") {
        if (selectedThingSchemaTemplates.value.length > 1) {
            return `Add ${selectedThingSchemaTemplates.value.length} Schemas`
        } else if (selectedThingSchemaTemplates.value.length > 0) {
            return `Add ${selectedThingSchemaTemplates.value[0].displayName}`;
        } else {
            return 'Add Template Schema';
        }
    } else {
        return "Add Blank Schema";
    }
});

const isAddDisabled = computed(() => {
    if (tab.value === "AddFromTemplate") {
        return selectedThingSchemaTemplates.value.length === 0;
    } else {
        const trimmedName = name.value.trim();
        return trimmedName.length === 0 || existingThingSchemaNames.includes(trimmedName);
    }
})

function cancel() {
    emit("cancelForm");
}

function add() {
    if (tab.value === "AddFromTemplate") {
        emit("addThingSchemaTemplates", selectedThingSchemaTemplates.value);
    } else {
        emit("addBlankThingSchema", name.value);
    }
}

async function loadTemplateThingSchemas() {
    await withLoadingScreen(async () => {
        const allThingSchemaTemplates = await manageSchemasAPI.fetchTemplateThingSchemas();
        thingSchemaTemplates.value = allThingSchemaTemplates.filter((template) => {
            return !existingThingSchemaNames.includes(template.name);
        });
    })
}

loadTemplateThingSchemas();

</script>
